/**
 * @file Create a new page linked to a chapter.
 */
import { Link, useNavigate } from "react-router-dom";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { canManageContent } from "@/lib/permissions";
import { slugify } from "@/lib/shared";

const RichTextEditor = lazy(() =>
  import("@/components/editor/RichTextEditor").then((m) => ({ default: m.RichTextEditor })),
);
const editorFallback = (
  <div className="rounded border border-zinc-200 bg-zinc-50 px-4 py-8 text-center text-sm text-zinc-600">
    Loading editor…
  </div>
);

interface ChapterOption {
  id: string;
  title: string;
  slug: string;
  guide: { title: string };
}

export function NewPagePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const allowed = useMemo(() => canManageContent(user?.roles), [user?.roles]);

  useEffect(() => {
    if (user && !canManageContent(user.roles)) {
      navigate("/dashboard/pages", { replace: true });
    }
  }, [user, navigate]);

  const [chapters, setChapters] = useState<ChapterOption[]>([]);
  const [chapterId, setChapterId] = useState("");
  const [title, setTitle] = useState("");
  const [slugSuffix, setSlugSuffix] = useState("");
  const [slugManual, setSlugManual] = useState(false);
  const [body, setBody] = useState("<p></p>");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void apiFetch<ChapterOption[]>("/api/v1/chapters").then((rows) => {
      setChapters(rows);
      if (rows[0]) {
        setChapterId(rows[0].id);
      }
    });
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!chapterId) {
      setError("Select a chapter.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const page = await apiFetch<{ id: string }>("/api/v1/pages", {
        method: "POST",
        json: {
          chapterId,
          title,
          ...(slugManual ? { slugSuffix } : {}),
          initialBody: body,
        },
      });
      navigate(`/dashboard/pages/${page.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create page");
    } finally {
      setSaving(false);
    }
  }

  if (user && !allowed) {
    return (
      <div className="text-zinc-500">
        Redirecting…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <Link to="/dashboard/pages" className="text-sm text-sky-600 hover:underline">
          ← All pages
        </Link>
        <h1 className="mt-2 text-2xl font-semibold">New page</h1>
        <p className="text-sm text-zinc-600">
          Choose a chapter and title. The URL adds a fixed id prefix plus an editable segment (filled from the title
          until you change it).
        </p>
      </div>

      {error ? <p className="text-sm text-rose-600">{error}</p> : null}

      <form onSubmit={submit} className="space-y-4">
        <label className="block text-sm">
          <span className="text-zinc-600">Chapter</span>
          <select
            className="mt-1 w-full rounded border border-zinc-300 px-3 py-2"
            value={chapterId}
            onChange={(e) => setChapterId(e.target.value)}
            required
          >
            {chapters.length === 0 ? <option value="">Loading…</option> : null}
            {chapters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.guide.title} › {c.title} ({c.slug})
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="text-zinc-600">Title</span>
          <input
            className="mt-1 w-full rounded border border-zinc-300 px-3 py-2"
            value={title}
            onChange={(e) => {
              const v = e.target.value;
              setTitle(v);
              if (!slugManual) {
                setSlugSuffix(slugify(v));
              }
            }}
            required
          />
        </label>

        <label className="block text-sm">
          <span className="text-zinc-600">URL segment (editable part)</span>
          <input
            className="mt-1 w-full rounded border border-zinc-300 px-3 py-2 font-mono text-sm"
            value={slugSuffix}
            onChange={(e) => {
              setSlugManual(true);
              setSlugSuffix(e.target.value.replace(/\s+/g, "-").toLowerCase());
            }}
            placeholder="auto from title"
            required
          />
          <span className="mt-1 block text-xs text-zinc-500">
            After save, the full path is <code className="rounded bg-zinc-100 px-1">xxxxxxxx-your-segment</code> — the
            8-character prefix comes from the page id and cannot change.
          </span>
        </label>

        <div>
          <div className="mb-1 text-sm text-zinc-600">Body</div>
          <Suspense fallback={editorFallback}>
            <RichTextEditor content={body} onChange={setBody} />
          </Suspense>
        </div>

        <button
          type="submit"
          disabled={saving || chapters.length === 0}
          className="rounded bg-zinc-900 px-4 py-2 text-sm text-white disabled:opacity-50"
        >
          {saving ? "Creating…" : "Create page"}
        </button>
      </form>
    </div>
  );
}
